import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Link, Show, VStack} from 'platform/foundation';

import {isEmpty} from 'ramda';

import {
  useGetChatSettingsQuery,
  useGetContextTemplateListQuery,
  usePutChatSettingsMutation,
} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError, useTemplatesByLocale} from '@dms/shared';

import {ApiError, composePath} from 'shared/web';

import {ChatSettingsFormSchema, ChatSettingsFormSchemaType} from './ChatSettingsFormSchema';

const DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS = 24;

export function ChatSettingsForm() {
  const {
    data: chatSettings,
    isLoading: isChatSettingsLoading,
    isError: isChatSettingsError,
  } = useGetChatSettingsQuery();

  const {
    data: templateData,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useGetContextTemplateListQuery({
    type: 'chat',
    context: 'business-case',
  });

  const templatesByLocale = useTemplatesByLocale(templateData);

  const welcomeMessageTemplateOptions = templatesByLocale.isGrouped
    ? templatesByLocale.groups.map((group) => ({
        label: group.label,
        options: group.templates.map((template) => ({
          label: template.name,
          value: template.id,
        })),
      }))
    : templatesByLocale.templates.map((template) => ({
        label: template.name,
        value: template.id,
      }));

  const [putChatSettings] = usePutChatSettingsMutation();

  const handleSubmit: FormSubmitHandler<ChatSettingsFormSchemaType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await putChatSettings({
      chatSettingsRequestBody: {
        conversationSessionTimeoutHours: values.conversationSessionTimeoutHours,
        welcomeMessageTemplateId: values.welcomeMessageTemplateId,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: ChatSettingsFormSchemaType = {
    conversationSessionTimeoutHours:
      chatSettings?.conversationSessionTimeoutHours ?? DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS,
    welcomeMessageTemplateId: chatSettings?.welcomeMessageTemplateId ?? null,
  };

  const isLoading = isChatSettingsLoading || isTemplateLoading;
  const isError = isChatSettingsError || isTemplateError;

  return (
    <SettingsSection>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Form<ChatSettingsFormSchemaType>
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
          experimentalZodSchema={ChatSettingsFormSchema}
        >
          {(control, formApi, _navigateWithoutBlocker) => {
            const handleDiscardChanges = () => formApi.reset(defaultValues);

            return (
              <Grid spacing={4} columns={2}>
                <FormField
                  name="conversationSessionTimeoutHours"
                  type="number"
                  label={i18n.t('entity.chatSettings.labels.conversationSessionTimeoutHours')}
                  suffix={i18n.t('general.labels.hours')}
                  control={control}
                  tooltip={i18n.t(
                    'entity.chatSettings.labels.conversationSessionTimeoutHoursTooltip'
                  )}
                />

                <VStack spacing={1}>
                  <FormField
                    name="welcomeMessageTemplateId"
                    control={control}
                    type="choice"
                    options={welcomeMessageTemplateOptions}
                    label={i18n.t('entity.chatSettings.labels.welcomeMessageTemplateId')}
                    tooltip={i18n.t('entity.chatSettings.labels.welcomeMessageTemplateIdTooltip')}
                  />

                  <Show when={isEmpty(templateData)}>
                    <Link
                      size="small"
                      title={i18n.t('entity.chatSettings.labels.welcomeMessageTemplateEmpty')}
                      href={composePath(settingsRoutes.messageTemplates)}
                    />
                  </Show>
                </VStack>

                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      variant: 'secondary',
                      title: i18n.t('general.actions.discardChanges'),
                      onClick: handleDiscardChanges,
                    },
                    {
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                      control,
                    },
                  ]}
                />
              </Grid>
            );
          }}
        </Form>
      </DataStatus>
    </SettingsSection>
  );
}
